'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, MB, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/ml-extra'

const ThresholdExplorer = dynamic(() => import('../_components/widgets/MLExtraWidgets').then(m => m.ThresholdExplorer), { ssr: false })

export default function EvaluatingModels() {
  return (
    <Lesson>
      <Step>
        <H>When accuracy lies</H>
        <P>
          Accuracy, the fraction of answers that are right, sounds like the obvious score. But suppose 1 in 20 transactions
          is fraud and a model simply says “not fraud” every time:
        </P>
        <Exercise id="accuracy-paradox" title="The lazy fraud detector" {...EX.accuracyParadox} />
        <P>
          95% accuracy, and it catches nothing. When one class is rare, accuracy hides the failures that matter. We need
          sharper tools.
        </P>
      </Step>

      <Step gate="q-precision">
        <H>Four kinds of outcome</H>
        <P>
          Every yes/no prediction lands in one of four boxes, the <strong>confusion matrix</strong>. A{' '}
          <strong>true positive</strong> is a fraud that was caught. A <strong>false positive</strong> is an innocent purchase
          that got flagged. A <strong>false negative</strong> is a fraud that slipped through. A <strong>true negative</strong>{' '}
          is an innocent purchase correctly let through. Two scores summarise them:
        </P>
        <MB>{'\\text{precision} = \\frac{TP}{TP + FP} \\qquad \\text{recall} = \\frac{TP}{TP + FN}'}</MB>
        <UL>
          <li>
            <strong>Precision</strong>: of everything the model flagged, how much was really positive?
          </li>
          <li>
            <strong>Recall</strong>: of everything really positive, how much did the model catch?
          </li>
        </UL>
        <Quiz
          id="q-precision"
          question="For a cancer screening test, which mistake is usually worse — and which score should be high?"
          options={[
            { label: 'Missing a real case (false negative) — so recall should be high', correct: true },
            { label: 'Flagging a healthy person (false positive) — so precision should be high', why: 'A false alarm means an extra check. A missed cancer can cost a life. Screening favours recall.' },
            { label: 'Both mistakes are equally bad', why: 'Rarely true in practice. The costs of the two mistakes are almost always different.' },
          ]}
          explain="A spam filter is the opposite: losing a real email (false positive) is worse than letting one spam through, so precision matters more."
        />
      </Step>

      <Step gate="metrics">
        <H>Compute them</H>
        <Exercise
          id="metrics"
          title="confusion and scores"
          {...EX.metrics}
          hints={[
            <>Loop over the pairs and increment the right counter for each combination of truth and prediction.</>,
            <>
              <C>{'const precision = c.tp + c.fp ? c.tp / (c.tp + c.fp) : 0'}</C> — the ternary guards against dividing by 0.
            </>,
          ]}
        />
        <Callout kind="notation">
          <strong>F1</strong> is the harmonic mean of precision and recall. It’s high only when <em>both</em> are high, so a
          model can’t game it by flagging everything (recall 1, precision tiny) or almost nothing.
        </Callout>
      </Step>

      <Step gate="q-threshold">
        <H>The threshold trade-off</H>
        <P>
          Most classifiers output a score or probability, and a <strong>threshold</strong> turns it into yes/no. Move the
          threshold and you trade one kind of mistake for the other. The <strong>ROC curve</strong> traces every possible
          threshold at once: how many positives you catch against how many negatives you falsely flag.
        </P>
        <Figure n="6.6" wide caption="Drag the threshold. Everything to its right is predicted positive. Switch to rare positives and watch accuracy stay high while precision collapses.">
          <ThresholdExplorer />
        </Figure>
        <Quiz
          id="q-threshold"
          question="You lower the threshold, so more things get flagged. What usually happens?"
          options={[
            { label: 'Recall goes up, precision goes down', correct: true },
            { label: 'Both go up', why: 'Flagging more catches more real cases (recall ↑) but also more false alarms (precision ↓).' },
            { label: 'Precision goes up, recall goes down', why: 'That’s what raising the threshold does.' },
          ]}
        />
      </Step>

      <Step gate="roc-auc">
        <H>One number for the whole curve</H>
        <P>
          The area under the ROC curve, <strong>AUC</strong>, scores a model across every threshold. It has a lovely meaning:
          the chance that a random positive example gets a higher score than a random negative one. 1 is perfect ranking; 0.5
          is no better than flipping a coin.
        </P>
        <Exercise
          id="roc-auc"
          title="auc"
          {...EX.rocAuc}
          hints={[
            <>
              Two nested loops: positives in the outer loop, negatives in the inner. Count <C>pairs</C>, and add 1 (or 0.5 for a
              tie) to <C>wins</C> when the positive’s score is higher.
            </>,
          ]}
        />
        <Callout kind="why">
          There’s no single “best” metric. Choosing what to measure is a decision about what matters. For large language
          models it gets even harder, and Module 10 has a whole lesson on it.
        </Callout>
      </Step>
    </Lesson>
  )
}
